"use client";
import { Avatar } from "@base-ui/react/avatar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { postCommentAction } from "@/actions/content.action";
import { uploadFile } from "@/actions/file.action";
import TopicReplyEditor from "@/app/(main)/forum/_components/topic-reply-editor";

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface Props {
  contentId: string;
  isLoggedIn: boolean;
  user?: CurrentUser;
}

const CommentInput = ({ contentId, isLoggedIn, user }: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let imageUrls: string[] = [];
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((file) => uploadFile(file, "content/comments")),
        );
        imageUrls = results.flatMap((r) => (r.isSuccess ? [r.result.filePath] : []));
      }
      return postCommentAction(contentId, html, imageUrls);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ["comments", contentId] });
      }
    },
  });

  if (!isLoggedIn) {
    return (
      <p className="py-4 text-sm text-neutral-500">
        Yorum yapmak için{" "}
        <Link
          className="text-brand-blue-500 font-medium hover:underline"
          href="/auth/login"
        >
          giriş yapınız
        </Link>
      </p>
    );
  }

  const displayName = user ? `${user.name} ${user.surname}` : "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("");

  return (
    <div className="flex flex-row gap-3 py-4 items-start">
      <Avatar.Root className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 align-middle text-sm font-medium text-gray-900 select-none shrink-0">
        {user?.imageUrl && (
          <Avatar.Image
            className="size-full object-cover"
            src={user.imageUrl}
          />
        )}
        <Avatar.Fallback className="flex size-full items-center justify-center text-sm">
          {initials}
        </Avatar.Fallback>
      </Avatar.Root>
      <div className="flex-1">
        <TopicReplyEditor
          autoFocus={false}
          isPending={isPending}
          onSubmit={(html, images) => mutate({ html, images })}
        />
      </div>
    </div>
  );
};

export default CommentInput;
